// Copyright (c) AdCritter. All rights reserved.

import React, { FC } from 'react'

import { EuiSpacer } from '@elastic/eui'

import { AIPlanCustomSection, AIPlanMarketAnalysisSectionPreview, MarketAnalysis } from 'api/entities/AIPlanBlueprintPreview'
import AIPlannerMarkdownRenderer from 'pages/MasterCampaignPlanPage/new/components/AIPlannerMarkdownRenderer'

import ReadOnlyMarketAnalysisSection from './ReadOnlyMarketAnalysisSection'

interface ReadOnlyStructuredMarketAnalysisProps {
  marketAnalysis: MarketAnalysis
  marketAnalysisSection?: AIPlanMarketAnalysisSectionPreview | null
}

const competitorProfilesToMarkdown = (profiles: { name?: string; strategicThreatLevel?: string; overview?: string; strengths?: string; weaknesses?: string; strategicImplications?: string }[]): string => {
  return profiles
    .map(profile => {
      const parts: string[] = []
      parts.push(`### ${profile.name}`)
      if (profile.strategicThreatLevel) parts.push(`**Threat Level:** ${profile.strategicThreatLevel}`)
      if (profile.overview) parts.push(`${profile.overview}`)
      if (profile.strengths) parts.push(`**Strengths:** ${profile.strengths}`)
      if (profile.weaknesses) parts.push(`**Weaknesses:** ${profile.weaknesses}`)
      if (profile.strategicImplications) parts.push(`**Strategic Implications:** ${profile.strategicImplications}`)
      return parts.join('\n\n')
    })
    .join('\n\n---\n\n')
}

const geographicClustersToMarkdown = (clusters: { clusterName?: string; priorityLevel?: string; alignmentScore?: string; keyAreas?: string; strategicRationale?: string }[]): string => {
  return clusters
    .map(cluster => {
      const parts: string[] = []
      parts.push(`### ${cluster.clusterName}`)
      if (cluster.priorityLevel) parts.push(`**Priority Level:** ${cluster.priorityLevel}`)
      if (cluster.alignmentScore) parts.push(`**Alignment Score:** ${cluster.alignmentScore}`)
      if (cluster.keyAreas) parts.push(`**Key Areas:** ${cluster.keyAreas}`)
      if (cluster.strategicRationale) parts.push(`**Strategic Rationale:** ${cluster.strategicRationale}`)
      return parts.join('\n\n')
    })
    .join('\n\n---\n\n')
}

const marketChallengesToMarkdown = (challenges: { title?: string; severity?: string; description?: string; operationalImplications?: string }[]): string => {
  return challenges
    .map(challenge => {
      const parts: string[] = []
      parts.push(`### ${challenge.title}`)
      if (challenge.severity) parts.push(`**Severity:** ${challenge.severity}`)
      if (challenge.description) parts.push(`${challenge.description}`)
      if (challenge.operationalImplications) parts.push(`**Operational Implications:** ${challenge.operationalImplications}`)
      return parts.join('\n\n')
    })
    .join('\n\n---\n\n')
}

type CustomSectionPosition = 'aboveStrategicOverview' | 'aboveIndustryContext' | 'aboveCompetitiveLandscape' | 'aboveGeographicOpportunities' | 'aboveSeasonalPatterns' | 'aboveChallenges' | 'aboveInsightsSummary' | 'aboveBottom'

const ReadOnlyStructuredMarketAnalysis: FC<ReadOnlyStructuredMarketAnalysisProps> = ({ marketAnalysis, marketAnalysisSection }) => {

  const strategicOverviewFields = [
    { key: 'businessPositioning', label: 'Business Positioning', value: marketAnalysis.strategicOverview?.businessPositioning },
    { key: 'coreMarketChallengesAndOpportunities', label: 'Core Market Challenges & Opportunities', value: marketAnalysis.strategicOverview?.coreMarketChallengesAndOpportunities },
    { key: 'analyticalFramework', label: 'Analytical Framework', value: marketAnalysis.strategicOverview?.analyticalFramework }
  ]

  const industryContextFields = [
    { key: 'marketSizeAndGrowth', label: 'Market Size & Growth', value: marketAnalysis.industryContextAnalysis?.marketSizeAndGrowth },
    { key: 'transformativeTrends', label: 'Transformative Trends', value: marketAnalysis.industryContextAnalysis?.transformativeTrends },
    { key: 'regulatoryConsiderations', label: 'Regulatory Considerations', value: marketAnalysis.industryContextAnalysis?.regulatoryConsiderations },
    { key: 'seasonalImpacts', label: 'Seasonal Impacts', value: marketAnalysis.industryContextAnalysis?.seasonalImpacts },
    { key: 'relatedIndustryInfluences', label: 'Related Industry Influences', value: marketAnalysis.industryContextAnalysis?.relatedIndustryInfluences },
    { key: 'economicIndicators', label: 'Economic Indicators', value: marketAnalysis.industryContextAnalysis?.economicIndicators }
  ]

  const competitiveLandscapeTextFields = [
    { key: 'competitiveAssessmentMethodology', label: 'Competitive Assessment Methodology', value: marketAnalysis.competitiveLandscape?.competitiveAssessmentMethodology },
    { key: 'marketPositioningAnalysis', label: 'Market Positioning Analysis', value: marketAnalysis.competitiveLandscape?.marketPositioningAnalysis },
    { key: 'shareOfVoiceAnalysis', label: 'Share of Voice Analysis', value: marketAnalysis.competitiveLandscape?.shareOfVoiceAnalysis },
    { key: 'gapAnalysis', label: 'Gap Analysis', value: marketAnalysis.competitiveLandscape?.gapAnalysis },
    { key: 'competitivePositioningOpportunities', label: 'Competitive Positioning Opportunities', value: marketAnalysis.competitiveLandscape?.competitivePositioningOpportunities }
  ]

  const geographicOpportunitiesTextFields = [
    { key: 'geographicTargetingMethodology', label: 'Geographic Targeting Methodology', value: marketAnalysis.geographicOpportunities?.geographicTargetingMethodology },
    { key: 'regionalDemandAnalysis', label: 'Regional Demand Analysis', value: marketAnalysis.geographicOpportunities?.regionalDemandAnalysis },
    { key: 'competitiveDensityAnalysis', label: 'Competitive Density Analysis', value: marketAnalysis.geographicOpportunities?.competitiveDensityAnalysis },
    { key: 'demographicAnalysis', label: 'Demographic Analysis', value: marketAnalysis.geographicOpportunities?.demographicAnalysis },
    { key: 'environmentalConsiderations', label: 'Environmental Considerations', value: marketAnalysis.geographicOpportunities?.environmentalConsiderations }
  ]

  const seasonalPatternsFields = [
    { key: 'keySeasonalCycles', label: 'Key Seasonal Cycles', value: marketAnalysis.seasonalAndCyclicalPatterns?.keySeasonalCycles },
    { key: 'externalFactorAnalysis', label: 'External Factor Analysis', value: marketAnalysis.seasonalAndCyclicalPatterns?.externalFactorAnalysis },
    { key: 'demandVariationQuantification', label: 'Demand Variation Quantification', value: marketAnalysis.seasonalAndCyclicalPatterns?.demandVariationQuantification },
    { key: 'consumerBehaviorTrends', label: 'Consumer Behavior Trends', value: marketAnalysis.seasonalAndCyclicalPatterns?.consumerBehaviorTrends }
  ]

  const coreChallengesTextFields = [
    { key: 'challengePrioritizationAnalysis', label: 'Challenge Prioritization Analysis', value: marketAnalysis.coreChallengesInMarket?.challengePrioritizationAnalysis },
    { key: 'marketConstraintAnalysis', label: 'Market Constraint Analysis', value: marketAnalysis.coreChallengesInMarket?.marketConstraintAnalysis }
  ]

  const insightsSummaryFields = [
    { key: 'keyFindingsSynthesis', label: 'Key Findings Synthesis', value: marketAnalysis.insightsSummary?.keyFindingsSynthesis },
    { key: 'strategicDirection', label: 'Strategic Direction', value: marketAnalysis.insightsSummary?.strategicDirection },
    { key: 'businessSpecificStrategicInsights', label: 'Business-Specific Strategic Insights', value: marketAnalysis.insightsSummary?.businessSpecificStrategicInsights }
  ]

  const competitorProfiles = marketAnalysis.competitiveLandscape?.keyCompetitorProfiles ?? []
  const geographicClusters = marketAnalysis.geographicOpportunities?.opportunityScoringSystem ?? []
  const marketChallenges = marketAnalysis.coreChallengesInMarket?.coreChallengesInTheMarket ?? []

  const positions: CustomSectionPosition[] = ['aboveStrategicOverview', 'aboveIndustryContext', 'aboveCompetitiveLandscape', 'aboveGeographicOpportunities', 'aboveSeasonalPatterns', 'aboveChallenges', 'aboveInsightsSummary', 'aboveBottom']

  const standardSectionKeys = ['strategicOverview', 'industryContextAnalysis', 'competitiveLandscape', 'geographicOpportunities', 'seasonalAndCyclicalPatterns', 'coreChallengesInMarket', 'insightsSummary']

  const sectionNumbers: Record<string, number> = {}
  const customSectionStartNumbers: Record<string, number> = {}
  let counter = 1
  for (let i = 0; i < positions.length; i++) {
    const customCount = ((marketAnalysisSection as any)?.[positions[i]] ?? []).length
    customSectionStartNumbers[positions[i]] = counter
    counter += customCount
    if (i < standardSectionKeys.length) {
      sectionNumbers[standardSectionKeys[i]] = counter
      counter += 1
    }
  }

  const sectionTitleContent = (sectionName: string, titleKey: string, defaultTitle: string) => {
    const title = (marketAnalysisSection as any)?.[titleKey] ?? defaultTitle
    const number = sectionNumbers[sectionName]
    return (
      <span style={{ fontSize: '2rem', fontWeight: 700 }}>
        {number}. {title}
      </span>
    )
  }

  const customSectionTitleContent = (number: number, title: string) => (
    <span style={{ fontSize: '2rem', fontWeight: 700 }}>
      {number}. {title || 'Untitled Section'}
    </span>
  )

  const renderCustomSectionsAtPosition = (position: CustomSectionPosition) => {
    const sections: AIPlanCustomSection[] = (marketAnalysisSection as any)?.[position] ?? []
    const startNumber = customSectionStartNumbers[position]

    return (
      <>
        {sections
          .filter(s => !s.hideAll)
          .map((section, index, filtered) => (
            <React.Fragment key={index}>
              <div>
                {customSectionTitleContent(startNumber + index, section.title ?? '')}
                <div style={{ padding: 24 }}>
                  {section.content && <AIPlannerMarkdownRenderer content={section.content} />}
                </div>
              </div>
              {index < filtered.length - 1 && <EuiSpacer size='m' />}
            </React.Fragment>
          ))}
        <EuiSpacer size='m' />
      </>
    )
  }

  return (
    <>
      {renderCustomSectionsAtPosition('aboveStrategicOverview')}

      <div>
        {sectionTitleContent('strategicOverview', 'strategicOverviewTitle', 'Strategic Overview')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={strategicOverviewFields} />
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveIndustryContext')}

      <div>
        {sectionTitleContent('industryContextAnalysis', 'industryContextAnalysisTitle', 'Industry Context Analysis')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={industryContextFields} />
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveCompetitiveLandscape')}

      <div>
        {sectionTitleContent('competitiveLandscape', 'competitiveLandscapeTitle', 'Competitive Landscape')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={competitiveLandscapeTextFields} />
          {competitorProfiles.length > 0 && (
            <>
              <EuiSpacer size='l' />
              <AIPlannerMarkdownRenderer content={competitorProfilesToMarkdown(competitorProfiles)} />
            </>
          )}
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveGeographicOpportunities')}

      <div>
        {sectionTitleContent('geographicOpportunities', 'geographicOpportunitiesTitle', 'Geographic Opportunities')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={geographicOpportunitiesTextFields} />
          {geographicClusters.length > 0 && (
            <>
              <EuiSpacer size='l' />
              <AIPlannerMarkdownRenderer content={geographicClustersToMarkdown(geographicClusters)} />
            </>
          )}
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveSeasonalPatterns')}

      <div>
        {sectionTitleContent('seasonalAndCyclicalPatterns', 'seasonalPatternsTitle', 'Seasonal & Cyclical Patterns')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={seasonalPatternsFields} />
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveChallenges')}

      <div>
        {sectionTitleContent('coreChallengesInMarket', 'coreChallengesTitle', 'Core Challenges in Market')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={coreChallengesTextFields} />
          {marketChallenges.length > 0 && (
            <>
              <EuiSpacer size='l' />
              <AIPlannerMarkdownRenderer content={marketChallengesToMarkdown(marketChallenges)} />
            </>
          )}
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveInsightsSummary')}

      <div>
        {sectionTitleContent('insightsSummary', 'insightsSummaryTitle', 'Insights Summary')}
        <div style={{ padding: 24 }}>
          <ReadOnlyMarketAnalysisSection fields={insightsSummaryFields} />
        </div>
      </div>

      {renderCustomSectionsAtPosition('aboveBottom')}
    </>
  )
}

export default ReadOnlyStructuredMarketAnalysis
